import { useEffect, useState } from 'react';
import {
  createPartnerStore,
  listPartnerStores,
  removePartnerStore,
} from '../services/PartnerStoreCRUD';
import ModalPartnerStoreForm from './ModalPartnerStoreForm';
import { PartnerStore } from '../models/PartnerStore';
import { useIntl } from 'react-intl';
import { PartnerStoreRequest } from '../models/PartnerStoreRequest';
import { toast } from 'react-toastify';

const EMPTY_STORE: PartnerStore = {
  id: null,
  store: null,
  image: null,
  name: '',
  description: '',
  benefit: '',
  coupon: '',
  href: '',
};

enum PartnerStoreState {
  IDLE = 'idle',
  LOADING = 'loading',
}

export default function PartnerStoresList() {
  const intl = useIntl();
  const [state, setState] = useState<PartnerStoreState>(
    PartnerStoreState.LOADING
  );
  const [partnerStores, setPartnerStores] = useState<Array<PartnerStore>>([]);
  const [editingPartnerStore, setEditingPartnerStore] = useState(null);

  async function fetchPartnerStores() {
    setState(PartnerStoreState.LOADING);
    try {
      const { data: newlyFetchedPartnerStores } = await listPartnerStores();
      console.log({
        newlyFetchedPartnerStores,
      });
      setPartnerStores(newlyFetchedPartnerStores);
    } catch (error) {
      console.error('Error fetching partner stores:', error);
    } finally {
      setState(PartnerStoreState.IDLE);
    }
  }

  useEffect(() => {
    fetchPartnerStores();
  }, []);

  function onAddPartnerStore() {
    setEditingPartnerStore(EMPTY_STORE);
  }

  async function onRemovePartnerStore(partnerStore: PartnerStore) {
    if (!partnerStore.id) {
      console.warn('Cannot remove partner store without an ID:', partnerStore);
      return;
    }
    try {
      const hasConfirmed = window.confirm(
        intl.formatMessage(
          { id: 'partnerStores.confirmDelete' },
          { name: partnerStore.name }
        )
      );
      if (!hasConfirmed) {
        return;
      }
      await removePartnerStore(partnerStore?.id);
      setPartnerStores((prevStores) =>
        prevStores.filter((store) => store.id !== partnerStore.id)
      );
    } catch (error) {
      console.error('Error removing partner store:', error);
      toast.error(
        intl.formatMessage(
          { id: 'common.errorDeleting' },
          { name: partnerStore.name }
        )
      );
    }
  }

  function onSubmitted(partnerStore: PartnerStore) {
    if (partnerStore.id) {
      setPartnerStores((prevStores) =>
        prevStores.map((store) =>
          store.id === partnerStore.id ? partnerStore : store
        )
      );
    } else {
      setPartnerStores((prevStores) => [...prevStores, partnerStore]);
    }
    setEditingPartnerStore(null);
    toast.success(
      intl.formatMessage(
        { id: 'partnerStores.partnerStoreSubmitted' },
        { name: partnerStore.name }
      )
    );
    fetchPartnerStores();
  }

  return (
    <>
      <header className="d-flex justify-content-between align-items-center mb-8">
        <h2>{intl.formatMessage({ id: 'partnerStores.title' })}</h2>
        <button className="btn btn-primary" onClick={onAddPartnerStore}>
          {intl.formatMessage({ id: 'partnerStores.addParnerStore' })}
        </button>
      </header>
      {state === PartnerStoreState.LOADING && (
        <p>{intl.formatMessage({ id: 'partnerStores.loading' })}</p>
      )}
      {state === PartnerStoreState.IDLE && partnerStores.length === 0 && (
        <p>{intl.formatMessage({ id: 'partnerStores.noPartnerStores' })}</p>
      )}
      {state === PartnerStoreState.IDLE && partnerStores.length > 0 && (
        <div className="d-flex flex-column gap-4">
          {partnerStores.map((partnerStore) => (
            <div
              key={partnerStore.id}
              className="card shadow-sm px-6 py-6 d-flex flex-row justify-start gap-8 align-items-start"
            >
              <img
                src={partnerStore.image}
                alt={partnerStore.name}
                className="img-fluid rounded"
                style={{
                  width: '200px',
                  height: '130px',
                  objectFit: 'cover',
                  aspectRatio: '16/9',
                }}
              />
              <div className="flex-grow-1">
                <span className="badge badge-light-primary mb-2">
                  {partnerStore.store?.name}
                </span>
                <h5>{partnerStore.name}</h5>
                <p className="text-muted mb-1">{partnerStore.description}</p>
                <p dangerouslySetInnerHTML={{ __html: partnerStore.benefit }} />
                <a
                  href={partnerStore.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline-primary mb-4 d-block"
                >
                  {intl.formatMessage({ id: 'partnerStores.storeLink' })}
                </a>
                {partnerStore?.coupon && (
                  <div className="d-flex gap-2 align-items-baseline">
                    <strong>
                      {intl.formatMessage({ id: 'partnerStores.coupon' })}:
                    </strong>
                    <input
                      readOnly
                      type="text"
                      className="form-control form-control-sm mb-2 w-200px"
                      value={partnerStore.coupon}
                    />
                  </div>
                )}
              </div>
              <div className="d-flex gap-2 flex-column flex-shrink-0 align-items-shrink">
                <button
                  className="btn btn-light-primary btn-sm flex-shrink-0"
                  onClick={() => setEditingPartnerStore(partnerStore)}
                >
                  <span className="fa-icon fas fa-edit me-3"></span>
                  {intl.formatMessage({ id: 'partnerStores.edit' })}
                </button>
                <button
                  className="btn btn-light-danger btn-sm flex-shrink-0"
                  onClick={() => onRemovePartnerStore(partnerStore)}
                >
                  <span className="fa-icon fas fa-trash me-3"></span>
                  {intl.formatMessage({ id: 'partnerStores.delete' })}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ModalPartnerStoreForm
        visible={!!editingPartnerStore}
        onClose={() => setEditingPartnerStore(null)}
        onSubmitted={onSubmitted}
        partnerStore={editingPartnerStore}
      />
    </>
  );
}
